<template>
  <div class="blog-editor">
    <div class="row">
      <div class="col-lg-8">
        <!-- Title -->
        <div class="form-group mb-4">
          <label class="form-control-label text-white">Post Title</label>
          <input
            type="text"
            class="form-control form-control-lg"
            placeholder="Enter your blog post title..."
            v-model="form.title"
          />
        </div>

        <!-- Excerpt -->
        <div class="form-group mb-4">
          <label class="form-control-label text-white">Excerpt / Summary</label>
          <textarea
            class="form-control"
            rows="2"
            placeholder="A short summary of your post..."
            v-model="form.excerpt"
          ></textarea>
        </div>

        <!-- Toolbar -->
        <div class="editor-toolbar mb-2">
          <div class="btn-group" role="group">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertTag('b')"
              title="Bold"
            >
              <i class="fa fa-bold"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertTag('i')"
              title="Italic"
            >
              <i class="fa fa-italic"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertTag('u')"
              title="Underline"
            >
              <i class="fa fa-underline"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertHeading('h2')"
              title="Heading"
            >
              <i class="fa fa-header"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertTag('blockquote')"
              title="Quote"
            >
              <i class="fa fa-quote-left"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertList('ul')"
              title="Bullet List"
            >
              <i class="fa fa-list-ul"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertList('ol')"
              title="Numbered List"
            >
              <i class="fa fa-list-ol"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertLink"
              title="Link"
            >
              <i class="fa fa-link"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertImage"
              title="Image"
            >
              <i class="fa fa-image"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="insertParagraph"
              title="Paragraph"
            >
              <i class="fa fa-paragraph"></i>
            </button>
          </div>
          <button
            type="button"
            class="btn btn-sm ml-2"
            :class="showPreview ? 'btn-info' : 'btn-outline-info'"
            @click="showPreview = !showPreview"
          >
            <i class="fa fa-eye mr-1"></i>{{ showPreview ? 'Hide Preview' : 'Preview' }}
          </button>
        </div>

        <!-- Content Editor -->
        <div class="form-group mb-4">
          <label class="form-control-label text-white">Content (HTML)</label>
          <textarea
            ref="contentEditor"
            class="form-control content-editor"
            rows="15"
            placeholder="Write your blog post content here... You can use HTML tags."
            v-model="form.content"
          ></textarea>
        </div>

        <!-- Live Preview -->
        <div v-if="showPreview" class="card shadow mb-4">
          <div class="card-header bg-white">
            <h5 class="mb-0">
              <i class="fa fa-eye mr-2"></i>Live Preview
            </h5>
          </div>
          <div class="card-body blog-preview" v-html="form.content"></div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <card shadow class="mb-4">
          <h5 class="mb-3">Post Settings</h5>

          <!-- Author -->
          <div class="form-group">
            <label class="form-control-label">Author</label>
            <input
              type="text"
              class="form-control"
              placeholder="Author name"
              v-model="form.author"
            />
          </div>

          <!-- Date -->
          <div class="form-group">
            <label class="form-control-label">Date</label>
            <input
              type="date"
              class="form-control"
              v-model="form.date"
            />
          </div>

          <!-- Cover Image URL -->
          <div class="form-group">
            <label class="form-control-label">Cover Image URL</label>
            <input
              type="text"
              class="form-control"
              placeholder="https://example.com/image.jpg"
              v-model="form.coverImage"
            />
            <div v-if="form.coverImage" class="mt-2">
              <img :src="form.coverImage" class="img-fluid rounded" alt="Cover preview" />
            </div>
          </div>

          <!-- Tags -->
          <div class="form-group">
            <label class="form-control-label">Tags</label>
            <div class="tag-input-wrapper">
              <span
                v-for="(tag, index) in form.tags"
                :key="index"
                class="badge badge-primary mr-1 mb-1 tag-badge"
              >
                {{ tag }}
                <i class="fa fa-times ml-1 tag-remove" @click="removeTag(index)"></i>
              </span>
              <input
                type="text"
                class="form-control form-control-sm mt-1"
                placeholder="Add a tag and press Enter"
                v-model="newTag"
                @keydown.enter.prevent="addTag"
              />
            </div>
          </div>

          <!-- Status -->
          <div class="form-group">
            <label class="form-control-label">Status</label>
            <div class="d-flex align-items-center">
              <base-switch v-model="form.published"></base-switch>
              <span class="ml-2">{{ form.published ? 'Published' : 'Draft' }}</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-4">
            <base-button type="success" block @click="savePost">
              <i class="fa fa-save mr-2"></i>{{ isEditing ? 'Update Post' : 'Save Post' }}
            </base-button>
            <base-button
              v-if="isEditing"
              type="warning"
              block
              @click="cancelEdit"
              class="mt-2"
            >
              <i class="fa fa-times mr-2"></i>Cancel
            </base-button>
          </div>
        </card>

        <!-- Quick Tips -->
        <card shadow>
          <h5 class="mb-3"><i class="fa fa-lightbulb-o mr-2"></i>Editor Tips</h5>
          <ul class="mb-0 pl-3" style="font-size: 0.85rem;">
            <li class="mb-1">Use the toolbar to format text</li>
            <li class="mb-1">Toggle preview to see your post live</li>
            <li class="mb-1">Use <code>&lt;p&gt;</code> tags for paragraphs</li>
            <li class="mb-1">Add tags to categorize your posts</li>
            <li class="mb-1">Set status to Draft to save without publishing</li>
          </ul>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BlogEditor',
  props: {
    post: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        title: '',
        author: '',
        date: new Date().toISOString().split('T')[0],
        coverImage: '',
        excerpt: '',
        content: '',
        tags: [],
        published: false
      },
      newTag: '',
      showPreview: false
    }
  },
  computed: {
    isEditing() {
      return this.post && this.post._id
    }
  },
  watch: {
    post: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form = { ...val, tags: [...(val.tags || [])] }
        }
      }
    }
  },
  methods: {
    savePost() {
      if (!this.form.title.trim()) {
        alert('Please enter a post title.')
        return
      }
      if (!this.form.content.trim()) {
        alert('Please enter post content.')
        return
      }
      this.$emit('save', { ...this.form, tags: [...this.form.tags] })
      if (!this.isEditing) {
        this.resetForm()
      }
    },
    cancelEdit() {
      this.$emit('cancel')
    },
    resetForm() {
      this.form = {
        title: '',
        author: '',
        date: new Date().toISOString().split('T')[0],
        coverImage: '',
        excerpt: '',
        content: '',
        tags: [],
        published: false
      }
      this.newTag = ''
      this.showPreview = false
    },
    addTag() {
      const tag = this.newTag.trim().toLowerCase()
      if (tag && !this.form.tags.includes(tag)) {
        this.form.tags.push(tag)
      }
      this.newTag = ''
    },
    removeTag(index) {
      this.form.tags.splice(index, 1)
    },
    insertAtCursor(text) {
      const editor = this.$refs.contentEditor
      const start = editor.selectionStart
      const end = editor.selectionEnd
      const before = this.form.content.substring(0, start)
      const selected = this.form.content.substring(start, end)
      const after = this.form.content.substring(end)
      if (selected) {
        this.form.content = before + text.replace('$SELECTED', selected) + after
      } else {
        this.form.content = before + text + after
      }
      this.$nextTick(() => {
        editor.focus()
      })
    },
    insertTag(tag) {
      const editor = this.$refs.contentEditor
      const start = editor.selectionStart
      const end = editor.selectionEnd
      const selected = this.form.content.substring(start, end) || 'text'
      this.insertAtCursor(`<${tag}>${selected}</${tag}>`)
    },
    insertHeading(level) {
      const editor = this.$refs.contentEditor
      const start = editor.selectionStart
      const end = editor.selectionEnd
      const selected = this.form.content.substring(start, end) || 'Heading'
      this.insertAtCursor(`<${level}>${selected}</${level}>`)
    },
    insertList(type) {
      this.insertAtCursor(`<${type}>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</${type}>`)
    },
    insertLink() {
      const url = prompt('Enter URL:', 'https://')
      if (url) {
        const editor = this.$refs.contentEditor
        const start = editor.selectionStart
        const end = editor.selectionEnd
        const selected = this.form.content.substring(start, end) || 'Link text'
        this.insertAtCursor(`<a href="${url}" target="_blank">${selected}</a>`)
      }
    },
    insertImage() {
      const url = prompt('Enter image URL:', 'https://')
      if (url) {
        this.insertAtCursor(`<img src="${url}" class="img-fluid rounded" alt="Blog image" />`)
      }
    },
    insertParagraph() {
      const editor = this.$refs.contentEditor
      const start = editor.selectionStart
      const end = editor.selectionEnd
      const selected = this.form.content.substring(start, end) || 'Your text here...'
      this.insertAtCursor(`<p>${selected}</p>`)
    }
  }
}
</script>

<style scoped>
.content-editor {
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  min-height: 350px;
  resize: vertical;
}

.blog-preview {
  font-size: 1rem;
  line-height: 1.8;
}

.blog-preview h2 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.blog-preview p {
  margin-bottom: 1rem;
}

.blog-preview blockquote {
  border-left: 4px solid #5e72e4;
  padding-left: 1rem;
  color: #525f7f;
  font-style: italic;
}

.blog-preview img {
  max-width: 100%;
  border-radius: 0.375rem;
  margin: 1rem 0;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.tag-badge {
  font-size: 0.8rem;
  padding: 0.35em 0.65em;
  cursor: default;
}

.tag-remove {
  cursor: pointer;
  opacity: 0.7;
}

.tag-remove:hover {
  opacity: 1;
}
</style>
